import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2 } from 'lucide-react';
import { TableSearchInput } from '@/components/TableSearchInput';
import { SnapshotChangePill } from '@/components/snapshot/SnapshotChangePill';
import { WorkspaceProductIdentityCell } from '@/components/workspace/WorkspaceProductIdentityCell';
import { cn } from '@/utils/cn';
import { formatDateRangeLabel } from '@/utils/dateRangePicker';
import {
  buildProductReport,
  rankProductReport,
  type ProductReportAdRow,
  type ProductReportFilter,
  type ProductReportMetric,
  type ProductReportPeriodValues,
  type ProductReportProduct,
  type RankedProductReportRow,
} from '@/utils/productReport';
import { clipRangeToVendorLag2, rollupSalesByAsin } from '@/utils/vendorRetailMetrics';
import {
  adsApi,
  spApiOauthApi,
  type SpApiAsinRow,
  type SpApiSalesAsinRow,
} from '@/api/client';
import type { DateRange } from '@/api/reportDateRanges';
import { formatSnapshotCurrency } from '@/api/snapshotMetrics';

const FILTERS: Array<{ id: ProductReportFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'winners', label: 'Winners' },
  { id: 'losers', label: 'Losers' },
];

const TABLES: Array<{ metric: ProductReportMetric; title: string }> = [
  { metric: 'total', title: 'Total Revenue' },
  { metric: 'ad', title: 'Ad Revenue' },
  { metric: 'organic', title: 'Organic Revenue' },
];

const PAGE_SIZE = 100;
const MAX_ROWS = 2000;

type ProductReportData = {
  vendorMapped: boolean;
  products: ProductReportProduct[];
  truncated: boolean;
};

function readNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function parseAdvertisedSales(data: unknown): { rows: ProductReportAdRow[]; totalCount: number } {
  if (!data || typeof data !== 'object') return { rows: [], totalCount: 0 };
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.rows) ? raw.rows : [];
  const rows: ProductReportAdRow[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const asin = typeof row.asin === 'string' ? row.asin.trim() : '';
    if (!asin) continue;
    const metrics =
      row.adMetrics && typeof row.adMetrics === 'object'
        ? (row.adMetrics as Record<string, unknown>)
        : {};
    const productName = typeof row.productName === 'string' ? row.productName.trim() : '';
    const imageUrl = typeof row.imageUrl === 'string' ? row.imageUrl.trim() : '';
    rows.push({
      asin,
      productName: productName || asin,
      imageUrl: imageUrl || null,
      sales: readNumber(metrics.sales30d),
    });
  }
  const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : rows.length;
  return { rows, totalCount };
}

async function loadAdvertisedSales(profileId: string, startDate: string, endDate: string) {
  const rows: ProductReportAdRow[] = [];
  let offset = 0;
  let totalCount = 0;
  while (offset < MAX_ROWS) {
    const res = await adsApi.getWorkspaceAdvertisedProductsPage(profileId, {
      limit: PAGE_SIZE,
      offset,
      startDate,
      endDate,
    });
    if (!res.success) throw new Error('Failed to load advertised products');
    const parsed = parseAdvertisedSales(res.data);
    totalCount = parsed.totalCount;
    rows.push(...parsed.rows);
    if (parsed.rows.length === 0) break;
    offset += parsed.rows.length;
    if (parsed.rows.length < PAGE_SIZE || offset >= totalCount) break;
  }
  return { rows, truncated: totalCount > rows.length };
}

async function loadVendorSide(profileId: string, current: DateRange, previous: DateRange, isDemo: boolean) {
  if (isDemo) {
    return { vendorMapped: false, currentSales: [], previousSales: [], catalog: [] };
  }
  const resolved = await spApiOauthApi.resolveConnection({ profileId });
  if (!resolved.success || !resolved.data) throw new Error('Failed to resolve Vendor connection');
  const listed = resolved.data.connections ?? [];
  const connections =
    listed.length > 0 ? listed : resolved.data.connection ? [resolved.data.connection] : [];
  if (connections.length === 0) {
    return { vendorMapped: false, currentSales: [], previousSales: [], catalog: [] };
  }

  const loadRange = async (range: DateRange) => {
    const clipped = clipRangeToVendorLag2(range.startDate, range.endDate);
    if (clipped.startDate > clipped.endDate) return [] as SpApiSalesAsinRow[];
    const groups = await Promise.all(
      connections.map(async (connection) => {
        const res = await spApiOauthApi.getSales(connection.id, {
          start: clipped.startDate,
          end: clipped.endDate,
        });
        if (!res.success || !res.data) throw new Error('Failed to load retail sales');
        return res.data.sales_by_asin ?? [];
      })
    );
    return groups.flat();
  };

  const [currentRows, previousRows, catalogs] = await Promise.all([
    loadRange(current),
    loadRange(previous),
    Promise.all(
      connections.map(async (connection) => {
        const res = await spApiOauthApi.getAsins(connection.id);
        if (!res.success || !res.data) return [] as SpApiAsinRow[];
        return res.data.asins ?? [];
      })
    ),
  ]);

  return {
    vendorMapped: true,
    currentSales: rollupSalesByAsin(currentRows).map((row) => ({
      asin: row.asin,
      orderedRevenue: row.orderedRevenue,
    })),
    previousSales: rollupSalesByAsin(previousRows).map((row) => ({
      asin: row.asin,
      orderedRevenue: row.orderedRevenue,
    })),
    catalog: catalogs.flat().map((row) => ({
      asin: row.asin,
      title: row.title,
      imageUrl: row.imageUrl,
    })),
  };
}

async function loadProductReport(
  profileId: string,
  current: DateRange,
  previous: DateRange,
  isDemo: boolean
): Promise<ProductReportData> {
  const [currentAds, previousAds, vendor] = await Promise.all([
    loadAdvertisedSales(profileId, current.startDate, current.endDate),
    loadAdvertisedSales(profileId, previous.startDate, previous.endDate),
    loadVendorSide(profileId, current, previous, isDemo),
  ]);
  return {
    vendorMapped: vendor.vendorMapped,
    truncated: currentAds.truncated || previousAds.truncated,
    products: buildProductReport({
      vendorMapped: vendor.vendorMapped,
      currentAds: currentAds.rows,
      previousAds: previousAds.rows,
      currentVendorSales: vendor.currentSales,
      previousVendorSales: vendor.previousSales,
      catalog: vendor.catalog,
    }),
  };
}

function MoneyCell({
  value,
  change,
}: {
  value: number | null;
  change?: ProductReportPeriodValues | null;
}) {
  const comparable = change != null && change.current != null && change.previous != null && change.previous !== 0;
  return (
    <td className="border-b border-border px-1 py-1 text-right align-middle">
      <div className="tabular-nums text-foreground">{formatSnapshotCurrency(value)}</div>
      {comparable ? (
        <div className="mt-0.5 flex justify-end">
          <SnapshotChangePill current={change.current ?? 0} previous={change.previous ?? 0} variant="plain" />
        </div>
      ) : null}
    </td>
  );
}

function emptyMessage(filter: ProductReportFilter, search: string): string {
  if (search.trim()) return 'No products match this search.';
  if (filter === 'winners') return 'No products increased in this range.';
  if (filter === 'losers') return 'No products decreased in this range.';
  return 'No products for this period.';
}

function RevenueTable({
  title,
  rows,
  metric,
  previousLabel,
  currentLabel,
  empty,
}: {
  title: string;
  rows: RankedProductReportRow[];
  metric: ProductReportMetric;
  previousLabel: string;
  currentLabel: string;
  empty: string;
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-surface">
      <h3 className="bg-brand px-3 py-2 text-sm font-semibold text-brand-foreground">{title}</h3>
      {rows.length === 0 ? (
        <p className="px-3 py-8 text-center text-sm text-foreground-muted">{empty}</p>
      ) : (
        <div className="max-h-[28rem] overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-xs [&_.app-table-identity]:gap-2 [&_.app-table-identity-thumb]:h-8 [&_.app-table-identity-thumb]:w-8 [&_.app-table-identity-thumb]:min-w-8">
            <thead>
              <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-foreground-muted">
                <th className="sticky top-0 z-10 w-11 bg-surface px-1 py-1">Rank</th>
                <th className="sticky top-0 z-10 bg-surface px-1 py-1">Product Title</th>
                <th className="sticky top-0 z-10 w-[5.75rem] bg-surface px-1 py-1 text-right">
                  <span className="block">Prev Range</span>
                  <span className="mt-0.5 block whitespace-normal font-normal normal-case leading-tight tracking-normal">
                    {previousLabel}
                  </span>
                </th>
                <th className="sticky top-0 z-10 w-[5.75rem] bg-surface px-1 py-1 text-right">
                  <span className="block">Current Range</span>
                  <span className="mt-0.5 block whitespace-normal font-normal normal-case leading-tight tracking-normal">
                    {currentLabel}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.asin} className="border-b border-border">
                  <td className="border-b border-border px-1 py-1 text-center align-middle text-foreground-muted">
                    {row.rank}
                  </td>
                  <WorkspaceProductIdentityCell
                    productName={row.productName}
                    asin={row.asin}
                    imageUrl={row.imageUrl}
                    className="border-b border-border px-1 py-1"
                  />
                  <MoneyCell value={row[metric].previous} />
                  <MoneyCell value={row[metric].current} change={row[metric]} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UnmappedTable({ title, message }: { title: string; message: string }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-surface">
      <h3 className="bg-brand px-3 py-2 text-sm font-semibold text-brand-foreground">{title}</h3>
      <p className="px-3 py-8 text-center text-sm text-foreground-muted">{message}</p>
    </div>
  );
}

export function ProductReportSection({
  profileId,
  isDemo,
  current,
  previous,
}: {
  profileId: string;
  isDemo: boolean;
  current: DateRange;
  previous: DateRange;
}) {
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState<ProductReportFilter>('all');
  const [search, setSearch] = useState('');

  const reportQuery = useQuery({
    queryKey: [
      'product-report',
      profileId,
      current.startDate,
      current.endDate,
      previous.startDate,
      previous.endDate,
      isDemo,
    ],
    queryFn: () => loadProductReport(profileId, current, previous, isDemo),
    enabled: Boolean(profileId),
    staleTime: 60_000,
  });

  const previousLabel = formatDateRangeLabel(previous.startDate, previous.endDate);
  const currentLabel = formatDateRangeLabel(current.startDate, current.endDate);
  const products = reportQuery.data?.products ?? [];
  const vendorMapped = reportQuery.data?.vendorMapped ?? false;

  const ranked = useMemo(
    () => ({
      total: rankProductReport(products, 'total', filter, search),
      ad: rankProductReport(products, 'ad', filter, search),
      organic: rankProductReport(products, 'organic', filter, search),
    }),
    [products, filter, search]
  );

  const unmappedMessage = 'Link a Seller or Vendor Central account to see this revenue.';

  return (
    <section data-pdf-section>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-1 py-1 text-left"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronDown
          className={cn('h-4 w-4 text-foreground-muted transition-transform', open && 'rotate-180')}
          aria-hidden
        />
        <span className="text-sm font-semibold text-foreground">Product Report</span>
      </button>

      {open ? (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-border bg-surface p-0.5" role="group" aria-label="Product result">
              {FILTERS.map((option) => {
                const selected = option.id === filter;
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setFilter(option.id)}
                    className={cn(
                      'h-7 rounded-md px-2.5 text-xs font-medium transition-colors',
                      selected
                        ? 'bg-brand text-brand-foreground'
                        : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground'
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <TableSearchInput
              id="product-report-search"
              value={search}
              onChange={setSearch}
              placeholder="Search for ASIN, products..."
              className="w-full max-w-xs"
            />
          </div>

          {reportQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-foreground-muted">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              Loading product report…
            </div>
          ) : null}

          {reportQuery.isError ? (
            <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-6 text-center">
              <p className="text-sm text-error-ink">
                {reportQuery.error instanceof Error
                  ? reportQuery.error.message
                  : 'Failed to load product report'}
              </p>
              <button
                type="button"
                onClick={() => void reportQuery.refetch()}
                className="mt-3 text-sm font-semibold text-brand hover:underline"
              >
                Retry
              </button>
            </div>
          ) : null}

          {reportQuery.isSuccess ? (
            <>
              {reportQuery.data.truncated ? (
                <p className="text-xs text-foreground-muted">
                  Showing the first {MAX_ROWS} advertised products in one or both periods.
                </p>
              ) : null}
              <div className="grid items-start gap-4 xl:grid-cols-3">
                {TABLES.map((table) =>
                  table.metric !== 'ad' && !vendorMapped ? (
                    <UnmappedTable key={table.metric} title={table.title} message={unmappedMessage} />
                  ) : (
                    <RevenueTable
                      key={table.metric}
                      title={table.title}
                      rows={ranked[table.metric]}
                      metric={table.metric}
                      previousLabel={previousLabel}
                      currentLabel={currentLabel}
                      empty={emptyMessage(filter, search)}
                    />
                  )
                )}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
